import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  CancelWhatsAppSignupApiArg,
  CancelWhatsAppSignupApiArgSchema,
  CancelWhatsAppSignupApiResponse,
  CancelWhatsAppSignupApiResponseSchema,
  CompleteWhatsAppSignupApiArg,
  CompleteWhatsAppSignupApiArgSchema,
  CompleteWhatsAppSignupApiResponse,
  CompleteWhatsAppSignupApiResponseSchema,
  GetWhatsAppConnectionApiArg,
  GetWhatsAppConnectionApiArgSchema,
  GetWhatsAppConnectionApiResponse,
  GetWhatsAppConnectionApiResponseSchema,
  StartWhatsAppSignupApiArg,
  StartWhatsAppSignupApiArgSchema,
  StartWhatsAppSignupApiResponse,
  StartWhatsAppSignupApiResponseSchema,
} from './types';

const WHATSAPP_URL = '/dms/v1/tenant/integrated-service/whatsapp';

export const whatsAppConnectionApi = omneticApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (build) => ({
    getWhatsAppConnection: build.query<
      GetWhatsAppConnectionApiResponse,
      GetWhatsAppConnectionApiArg
    >({
      query: () => ({
        url: `${WHATSAPP_URL}/connection`,
      }),
      providesTags: ['WhatsAppConfig'],
      extraOptions: {
        requestSchema: GetWhatsAppConnectionApiArgSchema,
        responseSchema: GetWhatsAppConnectionApiResponseSchema,
      },
    }),
    startWhatsAppSignup: build.mutation<StartWhatsAppSignupApiResponse, StartWhatsAppSignupApiArg>({
      query: (queryArg) => ({
        url: `${WHATSAPP_URL}/signup`,
        method: 'POST',
        body: queryArg,
        queryArg,
      }),
      extraOptions: {
        requestSchema: StartWhatsAppSignupApiArgSchema,
        responseSchema: StartWhatsAppSignupApiResponseSchema,
      },
    }),
    // A completed signup stores new credentials, so the legacy config and the connection state
    // both change. A failed one changes neither.
    completeWhatsAppSignup: build.mutation<
      CompleteWhatsAppSignupApiResponse,
      CompleteWhatsAppSignupApiArg
    >({
      query: (queryArg) => {
        const {signupId, ...body} = queryArg;
        return {
          url: `${WHATSAPP_URL}/signup/${signupId}/complete`,
          method: 'POST',
          body,
          queryArg,
        };
      },
      invalidatesTags: invalidateOnSuccess(
        (result: CompleteWhatsAppSignupApiResponse | undefined) =>
          result?.state === 'completed' ? ['WhatsAppConfig'] : []
      ),
      extraOptions: {
        requestSchema: CompleteWhatsAppSignupApiArgSchema,
        responseSchema: CompleteWhatsAppSignupApiResponseSchema,
      },
    }),
    cancelWhatsAppSignup: build.mutation<
      CancelWhatsAppSignupApiResponse,
      CancelWhatsAppSignupApiArg
    >({
      query: (queryArg) => {
        const {signupId, ...body} = queryArg;
        return {
          url: `${WHATSAPP_URL}/signup/${signupId}/cancel`,
          method: 'POST',
          body,
          queryArg,
        };
      },
      extraOptions: {
        requestSchema: CancelWhatsAppSignupApiArgSchema,
        responseSchema: CancelWhatsAppSignupApiResponseSchema,
      },
    }),
  }),
});

export const {
  useGetWhatsAppConnectionQuery,
  useStartWhatsAppSignupMutation,
  useCompleteWhatsAppSignupMutation,
  useCancelWhatsAppSignupMutation,
} = whatsAppConnectionApi;
